/**
 * Utilities for transforming threshold catalog payloads into UI-ready models.
 */

import type {
  ApiThresholdCatalogDefaultStyle,
  ApiThresholdCatalogItem,
  ApiThresholdCatalogResponse,
  ApiThresholdCatalogSource,
  ApiThresholdCatalogStyle,
} from '@highimpact-frontend/api-types';

export type {
  ApiThresholdCatalogDefaultStyle,
  ApiThresholdCatalogItem,
  ApiThresholdCatalogResponse,
  ApiThresholdCatalogSource,
  ApiThresholdCatalogStyle,
} from '@highimpact-frontend/api-types';

// -- type definitions --

export type ThresholdKind =
  | 'constant'
  | 'affine'
  | 'polynomial'
  | 'piecewise'
  | 'circle'
  | 'rectangle'
  | 'mesh'
  | 'custom';

export type ThresholdVariableDataType =
  | 'number'
  | 'datetime'
  | 'category'
  | 'vector'
  | 'boolean'
  | 'string';

export type ThresholdVariableRole =
  | 'independent'
  | 'dependent'
  | 'auxiliary'
  | 'time';

export type ThresholdBoundKind = ApiThresholdCatalogItem['bound_kind'];

export interface ApiThresholdCatalogVariableSpecItem {
  variable_name: string;
  data_type: ThresholdVariableDataType;
  role: ThresholdVariableRole;
  dimension_ref: string;
  required?: boolean;
}

export interface ApiThresholdCatalogVariableSpec {
  variables: ApiThresholdCatalogVariableSpecItem[];
}

export interface ThresholdVariableModel {
  variableName: string;
  dataType: ThresholdVariableDataType;
  role: ThresholdVariableRole;
  dimensionRef: string;
  required: boolean;
}

export interface ThresholdResolvedStyle {
  color: string | null;
  dash: 'solid' | 'dot' | 'dash' | 'longdash' | 'dashdot' | null;
  width: number | null;
  fillColor: string | null;
  opacity: number | null;
  rule: Record<string, unknown> | null;
}

export interface ThresholdAvailableStyle extends ThresholdResolvedStyle {
  id: number;
  label: string;
  isDefault: boolean;
}

export interface UIThresholdModel {
  id: number;
  thresholdId: number;
  label: string;
  description: string;
  boundKind: ThresholdBoundKind;
  typeLabel: string;
  type: ThresholdKind;
  variables: string[];
  variableDetails: ThresholdVariableModel[];
  parameterSummary: string;
  rawParameters: Record<string, unknown>;
  isLegacy: boolean;
  evaluationCoordinateSystem: string;
  representationCoordinateSystem: string;
  representationMode: string;
  activeFrom: string | null;
  activeTo: string | null;
  defaultStyle: ThresholdResolvedStyle | null;
  availableStyles: ThresholdAvailableStyle[];
  sourceBindings: ApiThresholdCatalogSource[];
}

type ThresholdCatalogLike =
  | ApiThresholdCatalogResponse
  | ApiThresholdCatalogItem[];

// -- constants --

const THRESHOLD_TYPE_LABELS: Record<ThresholdKind, string> = {
  constant: 'Valeur fixe',
  affine: 'Relation linéaire',
  polynomial: 'Courbe polynomiale',
  piecewise: 'Par paliers',
  circle: 'Zone circulaire',
  rectangle: 'Zone rectangulaire',
  mesh: 'Maillage complexe',
  custom: 'Logique personnalisée',
};

const KNOWN_THRESHOLD_TYPES: ThresholdKind[] = [
  'constant',
  'affine',
  'polynomial',
  'piecewise',
  'circle',
  'rectangle',
  'mesh',
  'custom',
];

const KNOWN_VARIABLE_DATA_TYPES: ThresholdVariableDataType[] = [
  'number',
  'datetime',
  'category',
  'vector',
  'boolean',
  'string',
];

const KNOWN_VARIABLE_ROLES: ThresholdVariableRole[] = [
  'independent',
  'dependent',
  'auxiliary',
  'time',
];

// -- functions --

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isCatalogItemArray(
  value: ThresholdCatalogLike
): value is ApiThresholdCatalogItem[] {
  return Array.isArray(value);
}

function cloneUnknown<T>(value: T): T {
  if (Array.isArray(value)) {
    return value.map((entry) => cloneUnknown(entry)) as T;
  }

  if (isRecord(value)) {
    return Object.fromEntries(
      Object.entries(value).map(([key, entry]) => [key, cloneUnknown(entry)])
    ) as T;
  }

  return value;
}

function normalizeThresholdKind(type: string): ThresholdKind {
  return (KNOWN_THRESHOLD_TYPES as string[]).includes(type)
    ? (type as ThresholdKind)
    : 'custom';
}

function mapTypeToFrench(type: ThresholdKind): string {
  return THRESHOLD_TYPE_LABELS[type] ?? THRESHOLD_TYPE_LABELS.custom;
}

function readNumericLike(value: unknown): number | string | null {
  if (typeof value === 'number' || typeof value === 'string') {
    return value;
  }

  return null;
}

function getParamSummary(
  type: ThresholdKind,
  params: Record<string, unknown>
): string {
  switch (type) {
    case 'constant': {
      const constant = readNumericLike(params.c ?? params.value);
      return constant == null ? 'Paramètres variables' : `Valeur : ${constant}`;
    }
    case 'affine': {
      const slope = readNumericLike(params.a);
      const intercept = readNumericLike(params.b);

      if (slope == null || intercept == null) {
        return 'Paramètres variables';
      }

      return `Pente : ${slope}, Constante : ${intercept}`;
    }
    case 'piecewise': {
      const segments = params.segments;
      return Array.isArray(segments)
        ? `${segments.length} paliers définis`
        : 'Paramètres variables';
    }
    case 'circle': {
      const radius = readNumericLike(params.radius);
      return radius == null ? 'Paramètres variables' : `Rayon : ${radius}`;
    }
    default:
      return 'Paramètres variables';
  }
}

function normalizeVariableDetails(
  variableSpec: ApiThresholdCatalogItem['variable_spec']
): ThresholdVariableModel[] {
  if (!isRecord(variableSpec) || !Array.isArray(variableSpec.variables)) {
    return [];
  }

  return variableSpec.variables.flatMap((entry) => {
    if (!isRecord(entry)) {
      return [];
    }

    const variableName = entry.variable_name;
    const dataType = entry.data_type;
    const role = entry.role;
    const dimensionRef = entry.dimension_ref;

    if (
      typeof variableName !== 'string' ||
      typeof dataType !== 'string' ||
      typeof role !== 'string' ||
      typeof dimensionRef !== 'string' ||
      !(KNOWN_VARIABLE_DATA_TYPES as string[]).includes(dataType) ||
      !(KNOWN_VARIABLE_ROLES as string[]).includes(role)
    ) {
      return [];
    }

    return [
      {
        variableName,
        dataType: dataType as ThresholdVariableDataType,
        role: role as ThresholdVariableRole,
        dimensionRef,
        required: entry.required !== false,
      },
    ];
  });
}

function normalizeIsoDate(
  value: string | Date | null | undefined
): string | null {
  if (value == null) {
    return null;
  }

  const parsed = value instanceof Date ? new Date(value) : new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed.toISOString();
}

function normalizeParameters(
  parameters: ApiThresholdCatalogItem['parameters']
): Record<string, unknown> {
  return isRecord(parameters) ? cloneUnknown(parameters) : {};
}

function normalizeDash(
  dash: string | null | undefined
): ThresholdResolvedStyle['dash'] {
  const normalized = ['solid', 'dot', 'dash', 'longdash', 'dashdot'].includes(
    dash ?? ''
  )
    ? (dash as ThresholdResolvedStyle['dash'])
    : null;
  return normalized;
}

function normalizeStyle(
  style: ApiThresholdCatalogDefaultStyle | null | undefined
): ThresholdResolvedStyle | null {
  if (!style) {
    return null;
  }

  return {
    color: style.color ?? null,
    dash: normalizeDash(style.dash),
    width: style.width ?? null,
    fillColor: style.fill_color ?? null,
    opacity: style.opacity ?? null,
    rule: isRecord(style.rule) ? cloneUnknown(style.rule) : null,
  };
}

function normalizeAvailableStyles(
  styles: ApiThresholdCatalogItem['available_styles']
): ThresholdAvailableStyle[] {
  if (!Array.isArray(styles)) {
    return [];
  }

  return styles
    .filter((style): style is ApiThresholdCatalogStyle => {
      return Number.isFinite(style?.threshold_assignment_style_id);
    })
    .map((style) => ({
      id: style.threshold_assignment_style_id,
      label: style.label?.trim() || 'Style sans nom',
      isDefault: style.is_default ?? false,
      color: style.color ?? null,
      dash: normalizeDash(style.dash),
      width: style.width ?? null,
      fillColor: style.fill_color ?? null,
      opacity: style.opacity ?? null,
      rule: isRecord(style.rule) ? cloneUnknown(style.rule) : null,
    }));
}

function normalizeSourceBindings(
  sourceBindings: ApiThresholdCatalogItem['source_bindings']
): ApiThresholdCatalogSource[] {
  if (!Array.isArray(sourceBindings)) {
    return [];
  }

  return sourceBindings.flatMap((binding) => {
    if (
      Number.isFinite(binding?.measure_point_id) &&
      Number.isFinite(binding?.measure_type_id)
    ) {
      return [
        {
          measure_point_id: binding.measure_point_id,
          measure_type_id: binding.measure_type_id,
        },
      ];
    }

    return [];
  });
}

/**
 * Transforms one catalog item into the simplified UI model used by the
 * threshold selector form.
 *
 * @param item Raw threshold assignment returned by the catalog endpoint.
 * @returns A stable, UI-oriented representation with normalized fields.
 */
export function transformThresholdCatalogItem(
  item: ApiThresholdCatalogItem
): UIThresholdModel {
  const type = normalizeThresholdKind(item.threshold_type);
  const variableDetails = normalizeVariableDetails(item.variable_spec);
  const rawParameters = normalizeParameters(item.parameters);
  const activeTo = normalizeIsoDate(item.active_to);

  return {
    id: item.threshold_scope_assignment_id,
    thresholdId: item.threshold_id,
    label: item.name,
    description: item.description ?? '',
    boundKind: item.bound_kind,
    typeLabel: mapTypeToFrench(type),
    type,
    variables: variableDetails.map((variable) => variable.variableName),
    variableDetails,
    parameterSummary: getParamSummary(type, rawParameters),
    rawParameters,
    isLegacy: activeTo != null && new Date(activeTo).getTime() < Date.now(),
    evaluationCoordinateSystem: item.evaluation_coordinate_system,
    representationCoordinateSystem: item.representation_coordinate_system,
    representationMode: item.representation_mode,
    activeFrom: normalizeIsoDate(item.active_from),
    activeTo,
    defaultStyle: normalizeStyle(item.default_style),
    availableStyles: normalizeAvailableStyles(item.available_styles),
    sourceBindings: normalizeSourceBindings(item.source_bindings),
  };
}

/**
 * Transforms the full threshold catalog response into UI-ready models.
 * Discarded items are ignored defensively even if the backend already filters
 * them out.
 *
 * @param input Full API response or a raw list of catalog items.
 * @returns Simplified threshold models ready for selector rendering.
 */
export function transformThresholdCatalogResponse(
  input: ThresholdCatalogLike
): UIThresholdModel[] {
  const items: ApiThresholdCatalogItem[] = isCatalogItemArray(input)
    ? input
    : input.items ?? [];

  return items
    .filter((item) => !item.is_discarded)
    .map((item) => transformThresholdCatalogItem(item));
}
